'use strict'

import {
  PORTAL_PRESETS,
  applyPortalPresetDefaultsToFlatMap
} from './portal-presets.js'
import {
  syncPointsAltitudeFields
} from './portal-altitude-utils.js'
import {
  setByPath,
  propsMapToRows,
  sortProjectSettingsRows
} from './portal-waypoint-core-utils.js'
import {
  updatePointXmlByPath
} from './portal-point-map-utils.js'
import {
  isKmzPayloadFieldPath,
  syncKmzMissionPayloadScopesFromXml
} from './portal-kmz-payload-utils.js'
import {
  normalizeMissionFileName,
  getMissionFileExtension,
  buildLocationBasedMissionBaseName,
  getCanonicalLeafFieldFromPath
} from './portal-mini-utils.js'

const NON_EXPORT_PROJECT_FILENAME_PATH = 'project.filename'
const PROP_SORT_PREFIX_PRIORITY = [
  'name',
  'description',
  'ExtendedData',
  'coordinates',
  'Point.coordinates',
  'Point'
]
const DJI_DRONE_PRESETS = Array.isArray(PORTAL_PRESETS.options && PORTAL_PRESETS.options.djiDronePresets)
  ? PORTAL_PRESETS.options.djiDronePresets
  : []
const KMZ_DEFAULT_DRONE_PROFILE = String((PORTAL_PRESETS.defaults && PORTAL_PRESETS.defaults.kmzDroneProfile) || '77:1').trim() || '77:1'

const PROJECT_POINT_HEIGHT_PATH_ALIASES = [
  'Point.height',
  'wpml:height',
  'height',
  'template.wpml:height',
  'waylines.wpml:height'
]

const PROJECT_POINT_EXECUTE_HEIGHT_PATH_ALIASES = [
  'Point.executeHeight',
  'wpml:executeHeight',
  'executeHeight',
  'template.wpml:executeHeight',
  'waylines.wpml:executeHeight'
]

const PROJECT_POINT_ELLIPSOID_HEIGHT_PATH_ALIASES = [
  'wpml:ellipsoidHeight',
  'ellipsoidHeight',
  'template.wpml:ellipsoidHeight',
  'waylines.wpml:ellipsoidHeight'
]

const buildProjectAltitudeSyncContext = function (state, fnUpdateXmlByPath) {
  return {
    importMissionState: state.importMissionState || null,
    DEM_PROVIDER_PRIORITY: [],
    ALTITUDE_RULES: PORTAL_PRESETS.altitudeRules,
    POINT_HEIGHT_PATH_ALIASES: PROJECT_POINT_HEIGHT_PATH_ALIASES,
    POINT_EXECUTE_HEIGHT_PATH_ALIASES: PROJECT_POINT_EXECUTE_HEIGHT_PATH_ALIASES,
    POINT_ELLIPSOID_HEIGHT_PATH_ALIASES: PROJECT_POINT_ELLIPSOID_HEIGHT_PATH_ALIASES,
    NON_EXPORT_POINT_HEIGHT_PATH: 'project.height',
    fnIsDemProviderEnabled: function () { return true },
    fnSetByPath: setByPath,
    fnUpdatePointXmlByPath: function (point, path, value) {
      return updatePointXmlByPath(point, path, value, fnUpdateXmlByPath)
    },
    fnTemplateSupportsPointCoordinatesField: function () { return false }
  }
}

  const getProjectSettingsMapFromDocument = function (documentEl, {
    _,
    fnBuildProjectDocumentClone,
    fnPropsToMap,
    fnXmlToObj
  }) {
    const clone = fnBuildProjectDocumentClone(documentEl)
    if (!clone) return {}
    const map = fnPropsToMap(fnXmlToObj(clone))
    return _.pickBy(map, function (_value, path) {
      const p = String(path || '')
      if (!p) return false
      if (/(^|\.)(Point|coordinates)(\.|$)/.test(p)) return false
      return true
    })
  }

  const getProjectSettingScopes = function (state, {
    $
  }) {
    const importMissionState = state.importMissionState || null
    const importMissionType = String(state.importMissionType || '')
    if (!importMissionState || !importMissionState.documentEl) return []
    if (importMissionType === 'kmz') {
      const templateText = String($('[data-tpl-new-kmz-template]').text() || '').trim()
      const waylinesText = String($('[data-tpl-new-kmz-waylines]').text() || '').trim()
      if (!templateText || !waylinesText || !importMissionState.secondaryDocumentEl) return []
      const parser = new DOMParser()
      const templateDoc = parser.parseFromString(templateText, 'application/xml')
      const waylinesDoc = parser.parseFromString(waylinesText, 'application/xml')
      const templateDocumentEl = templateDoc.getElementsByTagName('Document')[0] || templateDoc.documentElement
      const waylinesDocumentEl = waylinesDoc.getElementsByTagName('Document')[0] || waylinesDoc.documentElement
      return [
        {
          id: 'template',
          templateDocumentEl: templateDocumentEl,
          currentDocumentEl: importMissionState.documentEl
        },
        {
          id: 'waylines',
          templateDocumentEl: waylinesDocumentEl,
          currentDocumentEl: importMissionState.secondaryDocumentEl
        }
      ]
    }
    const templateText = String($('[data-tpl-new-kml-pattern]').text() || '').trim()
    if (!templateText) return []
    const parser = new DOMParser()
    const templateDoc = parser.parseFromString(templateText, 'application/xml')
    const templateDocumentEl = templateDoc.getElementsByTagName('Document')[0] || templateDoc.documentElement
    return [{
      id: 'template',
      templateDocumentEl: templateDocumentEl,
      currentDocumentEl: importMissionState.documentEl
    }]
  }

  const buildProjectSettingsModel = function (state, {
    _,
    $,
    fnBuildProjectDocumentClone,
    fnPropsToMap,
    fnXmlToObj,
    normalizeMissionFileName: fnNormalizeMissionFileName,
    getMissionFileExtension: fnGetMissionFileExtension,
    NON_EXPORT_PROJECT_FILENAME_PATH
  }) {
    const importMissionType = String(state.importMissionType || '')
    const importKmlFilename = String(state.importKmlFilename || '')
    const nonExportProjectFilenamePath = String(NON_EXPORT_PROJECT_FILENAME_PATH)

    const scopes = getProjectSettingScopes(state, { $: $ })
    if (!scopes.length) return null
    const rowMap = {}
    const defaultsMap = {}
    const bindingsByPath = {}
    const scopeById = {}
    const scopeMaps = {}
    scopes.forEach((scope) => {
      scopeById[scope.id] = scope
      scopeMaps[scope.id] = {
        templateMap: applyPortalPresetDefaultsToFlatMap(getProjectSettingsMapFromDocument(scope.templateDocumentEl, {
          _: _,
          fnBuildProjectDocumentClone: fnBuildProjectDocumentClone,
          fnPropsToMap: fnPropsToMap,
          fnXmlToObj: fnXmlToObj
        })),
        currentMap: getProjectSettingsMapFromDocument(scope.currentDocumentEl, {
          _: _,
          fnBuildProjectDocumentClone: fnBuildProjectDocumentClone,
          fnPropsToMap: fnPropsToMap,
          fnXmlToObj: fnXmlToObj
        })
      }
    })
    const isKmz = importMissionType === 'kmz' && scopeMaps.template && scopeMaps.waylines
    if (isKmz) {
      const templatePaths = Object.keys(scopeMaps.template.templateMap)
      const waylinesPaths = Object.keys(scopeMaps.waylines.templateMap)
      const allPaths = _.uniq(templatePaths.concat(waylinesPaths))
      allPaths.forEach((path) => {
        const inTemplate = Object.prototype.hasOwnProperty.call(scopeMaps.template.templateMap, path)
        const inWaylines = Object.prototype.hasOwnProperty.call(scopeMaps.waylines.templateMap, path)
        const isShared = inTemplate && inWaylines
        const displayPath = isShared ? path : ((inTemplate ? 'template.' : 'waylines.') + path)
        const bindings = []
        if (inTemplate) bindings.push({ scopeId: 'template', path: path })
        if (inWaylines) bindings.push({ scopeId: 'waylines', path: path })
        bindingsByPath[displayPath] = bindings
        const defaultValue = inTemplate ? scopeMaps.template.templateMap[path] : scopeMaps.waylines.templateMap[path]
        defaultsMap[displayPath] = defaultValue
        if (isShared) {
          const hasTemplateCurrent = Object.prototype.hasOwnProperty.call(scopeMaps.template.currentMap, path)
          const hasWaylinesCurrent = Object.prototype.hasOwnProperty.call(scopeMaps.waylines.currentMap, path)
          if (hasTemplateCurrent && hasWaylinesCurrent && _.isEqual(scopeMaps.template.currentMap[path], scopeMaps.waylines.currentMap[path])) {
            rowMap[displayPath] = scopeMaps.template.currentMap[path]
          } else if (hasTemplateCurrent) {
            rowMap[displayPath] = scopeMaps.template.currentMap[path]
          } else if (hasWaylinesCurrent) {
            rowMap[displayPath] = scopeMaps.waylines.currentMap[path]
          } else {
            rowMap[displayPath] = defaultValue
          }
        } else if (inTemplate) {
          rowMap[displayPath] = Object.prototype.hasOwnProperty.call(scopeMaps.template.currentMap, path)
            ? scopeMaps.template.currentMap[path]
            : defaultValue
        } else {
          rowMap[displayPath] = Object.prototype.hasOwnProperty.call(scopeMaps.waylines.currentMap, path)
            ? scopeMaps.waylines.currentMap[path]
            : defaultValue
        }
      })
    } else {
      const firstScope = scopes[0]
      const firstMaps = scopeMaps[firstScope.id]
      Object.keys(firstMaps.templateMap).forEach((path) => {
        bindingsByPath[path] = [{ scopeId: firstScope.id, path: path }]
        defaultsMap[path] = firstMaps.templateMap[path]
        rowMap[path] = Object.prototype.hasOwnProperty.call(firstMaps.currentMap, path)
          ? firstMaps.currentMap[path]
          : firstMaps.templateMap[path]
      })
    }
    const projectFilename = fnNormalizeMissionFileName(importKmlFilename || ('mission' + fnGetMissionFileExtension(importMissionType)), importMissionType)
    rowMap[nonExportProjectFilenamePath] = projectFilename
    defaultsMap[nonExportProjectFilenamePath] = projectFilename
    bindingsByPath[nonExportProjectFilenamePath] = []

    return {
      rowMap: rowMap,
      defaultsMap: defaultsMap,
      scopeById: scopeById,
      bindingsByPath: bindingsByPath
    }
  }

  const findDjiDronePresetByEnumSub = function (presets, droneEnumValue, droneSubEnumValue) {
    const enumText = String(droneEnumValue === null || droneEnumValue === undefined ? '' : droneEnumValue).trim()
    const subText = String(droneSubEnumValue === null || droneSubEnumValue === undefined ? '' : droneSubEnumValue).trim()
    if (!enumText || !Array.isArray(presets) || !presets.length) return null
    return presets.find((item) => {
      if (!item || typeof item !== 'object') return false
      const itemEnum = String(item.droneEnumValue === null || item.droneEnumValue === undefined ? '' : item.droneEnumValue).trim()
      const itemSub = String(item.droneSubEnumValue === null || item.droneSubEnumValue === undefined ? '' : item.droneSubEnumValue).trim()
      return itemEnum === enumText && itemSub === subText
    }) || null
  }

  const getDjiDronePresetByProfileValue = function (presets, profileValue) {
    const profile = String(profileValue === null || profileValue === undefined ? '' : profileValue).trim()
    if (!profile || !Array.isArray(presets) || !presets.length) return null
    return presets.find((item) => {
      return String(item && item.value !== undefined ? item.value : '').trim() === profile
    }) || null
  }

  const resolveKmzDroneProfile = function (presets, defaultProfile, droneEnumValue, droneSubEnumValue) {
    const exact = findDjiDronePresetByEnumSub(presets, droneEnumValue, droneSubEnumValue)
    if (exact) {
      return {
        profile: String(exact.value || '').trim(),
        droneEnumValue: String(exact.droneEnumValue || '').trim(),
        droneSubEnumValue: String(exact.droneSubEnumValue || '').trim()
      }
    }
    const fallback = getDjiDronePresetByProfileValue(presets, defaultProfile) || presets[0] || null
    if (fallback) {
      return {
        profile: String(fallback.value || '').trim(),
        droneEnumValue: String(fallback.droneEnumValue || '').trim(),
        droneSubEnumValue: String(fallback.droneSubEnumValue || '').trim()
      }
    }
    return {
      profile: String(defaultProfile || '77:1').trim() || '77:1',
      droneEnumValue: '77',
      droneSubEnumValue: '1'
    }
  }

  const parseKmzDroneProfileValue = function (presets, defaultProfile, rawValue) {
    const text = String(rawValue === null || rawValue === undefined ? '' : rawValue).trim()
    if (!text) return resolveKmzDroneProfile(presets, defaultProfile, '', '')
    const exact = getDjiDronePresetByProfileValue(presets, text)
    if (exact) {
      return {
        profile: String(exact.value || '').trim(),
        droneEnumValue: String(exact.droneEnumValue || '').trim(),
        droneSubEnumValue: String(exact.droneSubEnumValue || '').trim()
      }
    }
    const matched = text.match(/^(\d+)(?::(\d+))?$/)
    if (matched) {
      return resolveKmzDroneProfile(presets, defaultProfile, matched[1], matched[2] || '')
    }
    return resolveKmzDroneProfile(presets, defaultProfile, '', '')
  }

  const openProjectSettingsEditor = function (state, {
    _,
    $,
    Swal,
    areaSwal,
    fnSetImportKmlFilename,
    fnT,
    fnTF,
    fnCaptureState,
    fnHistoryPush,
    fnRenderPropEditorRows,
    fnUpdateXmlByPath,
    fnGetMissionAltitudeContextFor3d,
    fnNormalizeAltitudeMode,
    fnRecalculateEgm96EllipsoidForPoints,
    fnScheduleRoute3dSharedSync,
    fnBuildProjectDocumentClone,
    fnPropsToMap,
    fnXmlToObj
  }) {
    const importMissionState = state.importMissionState || null
    const importMissionType = String(state.importMissionType || '')
    const nonExportProjectFilenamePath = String(NON_EXPORT_PROJECT_FILENAME_PATH)
    const kmzDefaultDroneProfile = String(KMZ_DEFAULT_DRONE_PROFILE)
    const fnResolveKmzDroneProfile = function (enumValue, subValue) {
      return resolveKmzDroneProfile(DJI_DRONE_PRESETS, kmzDefaultDroneProfile, enumValue, subValue)
    }
    const fnParseKmzDroneProfileValue = function (profileValue) {
      return parseKmzDroneProfileValue(DJI_DRONE_PRESETS, kmzDefaultDroneProfile, profileValue)
    }
    const fnFindDjiDronePresetByEnumSub = function (enumValue, subValue) {
      return findDjiDronePresetByEnumSub(DJI_DRONE_PRESETS, enumValue, subValue)
    }

    if (!importMissionState || !importMissionState.xmlDoc) {
      Swal.fire(fnT('project.no_editable_project'))
      return false
    }
    const model = buildProjectSettingsModel(state, {
      _: _,
      $: $,
      fnBuildProjectDocumentClone: fnBuildProjectDocumentClone,
      fnPropsToMap: fnPropsToMap,
      fnXmlToObj: fnXmlToObj,
      normalizeMissionFileName: normalizeMissionFileName,
      getMissionFileExtension: getMissionFileExtension,
      NON_EXPORT_PROJECT_FILENAME_PATH: nonExportProjectFilenamePath
    })
    if (!model) {
      Swal.fire(fnT('project.data_unavailable'))
      return false
    }
    const rows = sortProjectSettingsRows(propsMapToRows(model.rowMap, PROP_SORT_PREFIX_PRIORITY), NON_EXPORT_PROJECT_FILENAME_PATH)
    if (!rows.length) {
      Swal.fire(fnT('project.no_editable_field'))
      return false
    }
    const historyBefore = fnCaptureState()
    let dirty = false
    areaSwal.fire({
      title: fnTF('project.dialog_title', { type: (importMissionType === 'kmz' ? 'KMZ' : 'KML') }),
      showCancelButton: true,
      size: 'lg',
      confirmButtonText: fnT('common.ok'),
      cancelButtonText: fnT('common.cancel'),
      position: 'bottom-left',
      willClose: function () {
        if (dirty) {
          fnHistoryPush(historyBefore)
        }
      },
      didOpen: function (modal) {
        const $cont = $(modal).find('.swal2-html-container').addClass('text-start')
        $cont.empty()
        const fnBuildRefreshedFileName = async function (currentValue) {
          const currentName = normalizeMissionFileName(state.importKmlFilename || currentValue, importMissionType)
          const baseName = currentName.replace(/\.(kml|kmz)$/i, '')
          const suffixMatch = baseName.match(/(_\d{8}_\d{6}(?:_mod\d{8}_\d{6})?)$/i)
          const suffix = suffixMatch ? suffixMatch[1] : ''
          const missionBase = await buildLocationBasedMissionBaseName(importMissionState || {})
          return normalizeMissionFileName(missionBase + suffix, importMissionType)
        }
        const $inputs = fnRenderPropEditorRows($cont, rows, {
          idPrefix: 'project-edit',
          markDirty: false,
          readonlyPaths: [],
          templateDefaults: model.defaultsMap,
          compactSharedPrefix: true,
          stripNamespacePrefixes: true,
          kmzScopeContext: importMissionType === 'kmz' ? 'project' : '',
          kmzScopeBindingsByPath: importMissionType === 'kmz' ? model.bindingsByPath : null,
          helpTextByPath: {
            [nonExportProjectFilenamePath]: fnT('project.export_filename_help')
          },
          normalizeProjectFileName: function (raw) {
            return normalizeMissionFileName(raw, importMissionType)
          },
          refreshProjectFileName: fnBuildRefreshedFileName
        })

        if (importMissionType === 'kmz' && DJI_DRONE_PRESETS.length) {
          const $groupsParent = $cont.find('.container-fluid').first()
          const $enumInputs = $inputs.filter(function () {
            const rowPath = String($(this).attr('data-prop-path') || '')
            return getCanonicalLeafFieldFromPath(rowPath) === 'droneEnumValue'
          })
          const $subInputs = $inputs.filter(function () {
            const rowPath = String($(this).attr('data-prop-path') || '')
            return getCanonicalLeafFieldFromPath(rowPath) === 'droneSubEnumValue'
          })
          if ($groupsParent.length && $enumInputs.length) {
            const selectId = 'project-kmz-drone-profile'
            const $group = $('<div class="mb-2 row align-items-center"></div>')
            const $label = $('<label class="col-sm-5 col-form-label col-form-label-sm d-inline-flex align-items-center"></label>')
              .attr('for', selectId)
              .text('KMZ DJI drone profile')
            const $inputWrap = $('<div class="col-sm-7"></div>')
            const $select = $('<select class="form-select form-select-sm"></select>')
              .attr('id', selectId)
              .attr('data-project-kmz-drone-profile', '1')
            DJI_DRONE_PRESETS.forEach((preset) => {
              const profile = String(preset && preset.value !== undefined ? preset.value : '').trim()
              if (!profile) return
              const modelLabel = String(preset && preset.label !== undefined ? preset.label : profile).trim() || profile
              const enumCode = String(preset && preset.droneEnumValue !== undefined ? preset.droneEnumValue : '').trim()
              const subCode = String(preset && preset.droneSubEnumValue !== undefined ? preset.droneSubEnumValue : '').trim()
              const codeText = subCode ? (enumCode + '/' + subCode) : enumCode
              const optionText = codeText ? (modelLabel + ' (' + codeText + ')') : modelLabel
              $select.append($('<option></option>').val(profile).text(optionText))
            })
            const $help = $('<div class="form-text"></div>').text('Updates both droneEnumValue and droneSubEnumValue in template+waylines.')
            $inputWrap.append($select).append($help)
            $group.append($label).append($inputWrap)
            $groupsParent.prepend($group)

            const fnCurrentDroneSelection = function () {
              const enumValue = String($enumInputs.first().val() || '').trim()
              const subValue = $subInputs.length ? String($subInputs.first().val() || '').trim() : ''
              return fnResolveKmzDroneProfile(enumValue, subValue)
            }
            const fnSyncSelectFromInputs = function () {
              const resolved = fnCurrentDroneSelection()
              if (String($select.val() || '').trim() !== resolved.profile) {
                $select.val(resolved.profile)
              }
            }
            const fnApplyProfileToInputs = function (profileValue) {
              const resolved = fnParseKmzDroneProfileValue(profileValue)
              $enumInputs.each(function () {
                const $input = $(this)
                const nextValue = resolved.droneEnumValue
                if (String($input.val() || '').trim() === nextValue) return
                $input.val(nextValue)
                $input.trigger('input')
              })
              $subInputs.each(function () {
                const $input = $(this)
                const nextValue = resolved.droneSubEnumValue
                if (String($input.val() || '').trim() === nextValue) return
                $input.val(nextValue)
                $input.trigger('input')
              })
            }

            const hasKnownCurrent = fnFindDjiDronePresetByEnumSub(
              String($enumInputs.first().val() || '').trim(),
              $subInputs.length ? String($subInputs.first().val() || '').trim() : ''
            )
            if (!hasKnownCurrent) {
              fnApplyProfileToInputs(kmzDefaultDroneProfile)
            }
            fnSyncSelectFromInputs()
            $select.on('change', function () {
              fnApplyProfileToInputs($select.val())
              fnSyncSelectFromInputs()
            })
            $enumInputs.on('input change', fnSyncSelectFromInputs)
            $subInputs.on('input change', fnSyncSelectFromInputs)
          }
        }

        $inputs.on('input', function () {
          const $input = $(this)
          const displayPath = String($input.data('prop-path') || '')
          const bindings = Array.isArray(model.bindingsByPath[displayPath]) ? model.bindingsByPath[displayPath] : []
          const value = $input.val()
          if (displayPath === nonExportProjectFilenamePath) {
            const nextFileName = normalizeMissionFileName(value, importMissionType)
            fnSetImportKmlFilename(nextFileName)
            state.importKmlFilename = nextFileName
            $input.val(nextFileName)
            return
          }
          if (!bindings.length) return
          const previousAltContext = fnGetMissionAltitudeContextFor3d(importMissionState)
          let altitudeModeTouched = false
          let payloadTouched = false
          bindings.forEach((binding) => {
            const scope = model.scopeById[binding.scopeId]
            if (!scope || !scope.currentDocumentEl) return
            if (fnUpdateXmlByPath(scope.currentDocumentEl, binding.path, value)) {
              dirty = true
              const bindingPathLower = String(binding.path || '').toLowerCase()
              if (bindingPathLower.includes('heightmode') || bindingPathLower.includes('executeheightmode')) {
                altitudeModeTouched = true
              }
              if (importMissionType === 'kmz' && isKmzPayloadFieldPath(binding.path)) {
                payloadTouched = true
              }
            }
          })
          if (payloadTouched && importMissionType === 'kmz') {
            syncKmzMissionPayloadScopesFromXml(importMissionState, fnXmlToObj)
          }
          if (altitudeModeTouched && importMissionState && Array.isArray(importMissionState.waypoints)) {
            const nextAltContext = fnGetMissionAltitudeContextFor3d(importMissionState)
            const syncResult = syncPointsAltitudeFields(importMissionState.waypoints, {
              sourceMissionAltContext: previousAltContext,
              targetMissionAltContext: nextAltContext
            }, buildProjectAltitudeSyncContext(state, fnUpdateXmlByPath))
            if (syncResult.changedCount > 0) {
              dirty = true
            }
          }
          if (dirty) {
            fnScheduleRoute3dSharedSync()
          }
        })
      }
    })
    return true
  }

export {
  openProjectSettingsEditor
}
